import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CustomerService } from '../../../Services/customer.service';
import { Customer } from '../../../Models/customer.model';
import {
  Auftrag,
  AUFTRAG_STATUS_LABELS,
  AuftragStatus,
  MyStammkundeMembership,
  StammkundeMember,
  StammkundeWorker,
  TakedaEinsatzort,
  TakedaEinsatzortVorschlag,
  TakedaProjektnummer,
  WarehouseRef
} from '../../../Models/stammkunde.model';
import { AuthService } from '../../../api-authorization/authorize.service';
import { AuftragFormComponent } from '../auftrag-form/auftrag-form.component';
import { CustomerFormComponent } from '../../Wartung/customer-form/customer-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { TeamFormComponent } from '../team-form/team-form.component';
import { WorkerFormComponent } from '../worker-form/worker-form.component';
import { LagerLinkComponent } from '../lager-link/lager-link.component';

type AuftragFilter = 'alle' | 'offen' | 'fertig';

/** Aufträge mit diesen Status gelten als "fertig" (Rest = offen). */
const DONE_STATUSES: AuftragStatus[] = ['Completed', 'Priced', 'Billed'];

/**
 * Detailseite eines Stammkunden: Kennzahlen, Aufträge, Team (inkl. Arbeiter ohne
 * Login), verknüpfte Lager und Stammdaten. Alle Pflege-Aktionen laufen über Popups.
 */
@Component({
  selector: 'app-stammkunde-detail',
  templateUrl: './stammkunde-detail.component.html',
  styleUrls: ['./stammkunde-detail.component.css', '../stammkunden-theme.css']
})
export class StammkundeDetailComponent implements OnInit {
  customerId!: number;
  customer: Customer | null = null;
  auftraege: Auftrag[] = [];
  members: StammkundeMember[] = [];
  workers: StammkundeWorker[] = [];

  // ── Takeda-Stammdaten (Einsatzorte / Projektnummern) ──
  //
  // Geladen werden hier bewusst AUCH die deaktivierten Einträge: dies ist die
  // Pflegeansicht. Die Auswahlfelder am Auftrag laden nur die aktiven.
  einsatzorte: TakedaEinsatzort[] = [];
  projektnummern: TakedaProjektnummer[] = [];
  stammdatenLoading = false;
  stammdatenSaving = false;

  /** Eingabezeile für "+ Neuer Einsatzort". */
  neuerEinsatzort = '';
  neuerEinsatzortBeschreibung = '';

  /** Eingabezeile für "+ Projektnummer". */
  neueProjektnummer = '';
  neueProjektnummerBezeichnung = '';

  /** Übernahme aus "Anlagen & Verteiler" – Vorschläge und Auswahl. */
  uebernahmeVorschlaege: TakedaEinsatzortVorschlag[] | null = null;
  uebernahmeAuswahl = new Set<number>();
  uebernahmeLoading = false;
  linkedWarehouses: WarehouseRef[] = [];
  myMemberships: MyStammkundeMembership[] = [];

  loading = true;
  selectedTab = 0;
  auftragFilter: AuftragFilter = 'alle';

  readonly statusLabels = AUFTRAG_STATUS_LABELS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stammkundeService: StammkundeService,
    private customerService: CustomerService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.customerId = Number(params.get('id'));
      this.load();
    });
  }

  // ── Rechte ──

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  /** Bauleiter DIESES Stammkunden (laut Mitgliedschaft) oder Admin/SuperAdmin. */
  get canManage(): boolean {
    if (this.authService.isSuperAdmin() || this.authService.isAdmin()) return true;
    return this.myMemberships.some(m => m.customerId === this.customerId && m.role === 'Bauleiter');
  }

  /** Der Manager bekommt zusätzlich einen Übersicht-Tab (Freigaben/Auswertung) an Position 0. */
  get hasOverviewTab(): boolean {
    return this.canManage;
  }

  // Tab-Indizes verschieben sich, wenn der Übersicht-Tab vorangestellt ist.
  get tabAuftraege(): number { return this.hasOverviewTab ? 1 : 0; }
  get tabTeam(): number { return this.hasOverviewTab ? 2 : 1; }
  get tabLager(): number { return this.hasOverviewTab ? 3 : 2; }

  // ── Laden ──

  load(): void {
    this.loading = true;
    forkJoin({
      customer: this.customerService.getCustomer(this.customerId),
      auftraege: this.stammkundeService.getAuftraege(this.customerId),
      members: this.stammkundeService.getMembers(this.customerId),
      workers: this.stammkundeService.getWorkers(this.customerId),
      warehouses: this.stammkundeService.getLinkedWarehouses(this.customerId),
      permissions: this.authService.getMyPermissions()
    }).subscribe({
      next: result => {
        this.customer = result.customer;
        this.auftraege = result.auftraege ?? [];
        this.members = result.members ?? [];
        this.workers = result.workers ?? [];
        this.linkedWarehouses = result.warehouses ?? [];
        this.myMemberships = result.permissions?.stammkundenMemberships ?? [];
        this.loading = false;

        // Takeda-Stammdaten separat: fehlt der Zugriff oder ist der Kunde kein
        // Takeda-Kunde, bleibt der Bereich einfach leer.
        if (this.istTakeda) {
          this.ladeTakedaStammdaten();
        }
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden des Stammkunden', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  // ── Kennzahlen ──

  get offeneAuftraege(): number {
    return this.auftraege.filter(a => !DONE_STATUSES.includes(a.status)).length;
  }

  get gesamtEinsaetze(): number {
    return this.auftraege.reduce((sum, a) => sum + (a.assignmentCount ?? 0), 0);
  }

  get teamGroesse(): number {
    return this.members.length + this.workers.length;
  }

  goTab(index: number): void {
    this.selectedTab = index;
  }

  // ── Aufträge ──

  get filteredAuftraege(): Auftrag[] {
    switch (this.auftragFilter) {
      case 'offen': return this.auftraege.filter(a => !DONE_STATUSES.includes(a.status));
      case 'fertig': return this.auftraege.filter(a => DONE_STATUSES.includes(a.status));
      default: return this.auftraege;
    }
  }

  countFor(filter: AuftragFilter): number {
    switch (filter) {
      case 'offen': return this.offeneAuftraege;
      case 'fertig': return this.auftraege.length - this.offeneAuftraege;
      default: return this.auftraege.length;
    }
  }

  openAuftragForm(auftrag?: Auftrag): void {
    const dialogRef = this.dialog.open(AuftragFormComponent, {
      width: '760px',
      maxWidth: '96vw',
      data: {
        customerId: this.customerId,
        auftrag,
        linkedWarehouses: this.linkedWarehouses,
        // Beim Anlegen gibt es noch keinen Auftrag, aus dem sich der Takeda-Bezug
        // ableiten ließe – der Kunde trägt das Kennzeichen bereits.
        isTakeda: this.customer?.isTakeda === true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.reloadAuftraege();
        this.snackBar.open(auftrag ? 'Auftrag aktualisiert' : 'Auftrag angelegt', 'OK', { duration: 3000 });
      }
    });
  }

  openAuftrag(auftrag: Auftrag): void {
    this.router.navigate(['/stammkunden', this.customerId, 'auftrag', auftrag.id]);
  }

  openPlanung(): void {
    this.router.navigate(['/stammkunden', this.customerId, 'planung']);
  }

  editAuftrag(auftrag: Auftrag, event: Event): void {
    event.stopPropagation();
    this.openAuftragForm(auftrag);
  }

  deleteAuftrag(auftrag: Auftrag, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: {
        title: 'Auftrag löschen',
        message: `"${auftrag.title}" wirklich löschen? Die Einsätze bleiben erhalten, verlieren aber den Auftragsbezug.`
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.deleteAuftrag(auftrag.id).subscribe({
          next: () => {
            this.reloadAuftraege();
            this.snackBar.open('Auftrag gelöscht', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  private reloadAuftraege(): void {
    this.stammkundeService.getAuftraege(this.customerId).subscribe({
      next: auftraege => this.auftraege = auftraege ?? [],
      error: () => { /* Liste bleibt unverändert */ }
    });
  }

  statusLabel(status: AuftragStatus): string {
    return this.statusLabels[status] ?? status;
  }

  // ── Team (Popups) ──

  openTeamDialog(): void {
    const dialogRef = this.dialog.open(TeamFormComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, members: this.members }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.snackBar.open('Team gespeichert', 'OK', { duration: 3000 });
        this.stammkundeService.getMembers(this.customerId).subscribe({
          next: members => this.members = members ?? [],
          error: () => { /* Anzeige bleibt unverändert */ }
        });
      }
    });
  }

  openWorkerDialog(worker?: StammkundeWorker): void {
    const dialogRef = this.dialog.open(WorkerFormComponent, {
      width: '460px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, worker }
    });
    dialogRef.afterClosed().subscribe(saved => {
      if (saved) {
        this.snackBar.open(worker ? 'Arbeiter aktualisiert' : 'Arbeiter angelegt', 'OK', { duration: 3000 });
        this.stammkundeService.getWorkers(this.customerId).subscribe({
          next: workers => this.workers = workers ?? [],
          error: () => { /* Anzeige bleibt unverändert */ }
        });
      }
    });
  }

  deleteWorker(worker: StammkundeWorker, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Arbeiter entfernen',
        message: `"${worker.name}" wirklich entfernen? Er wird auch aus bereits geplanten Einsätzen entfernt.`
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.deleteWorker(this.customerId, worker.id).subscribe({
          next: () => {
            this.workers = this.workers.filter(w => w.id !== worker.id);
            this.snackBar.open('Arbeiter entfernt', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Entfernen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  // ── Lager (Popup) ──

  openLagerDialog(): void {
    const dialogRef = this.dialog.open(LagerLinkComponent, {
      width: '520px',
      maxWidth: '96vw',
      data: { customerId: this.customerId, linked: this.linkedWarehouses }
    });
    dialogRef.afterClosed().subscribe(linked => {
      if (linked) {
        this.snackBar.open('Lager verknüpft', 'OK', { duration: 3000 });
        this.reloadWarehouses();
      }
    });
  }

  unlinkWarehouse(warehouse: WarehouseRef): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Verknüpfung lösen',
        message: `Verknüpfung mit "${warehouse.name}" wirklich lösen?`
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.unlinkWarehouse(this.customerId, warehouse.id).subscribe({
          next: () => {
            this.reloadWarehouses();
            this.snackBar.open('Verknüpfung gelöst', 'OK', { duration: 3000 });
          },
          error: () => this.snackBar.open('Fehler beim Lösen der Verknüpfung', 'OK', { duration: 4000 })
        });
      }
    });
  }

  private reloadWarehouses(): void {
    this.stammkundeService.getLinkedWarehouses(this.customerId).subscribe({
      next: warehouses => this.linkedWarehouses = warehouses ?? [],
      error: () => { /* Anzeige bleibt unverändert */ }
    });
  }

  // ── Stammdaten ──

  editCustomer(): void {
    if (!this.customer) return;
    const dialogRef = this.dialog.open(CustomerFormComponent, {
      width: '880px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      data: { ...this.customer }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.load();
        this.snackBar.open('Stammdaten aktualisiert', 'OK', { duration: 3000 });
      }
    });
  }

  // ── Takeda-Stammdaten: Einsatzorte und Projektnummern ──

  /** Nur beim konfigurierten Takeda-Kunden gibt es diese Stammdaten. */
  get istTakeda(): boolean {
    return this.customer?.isTakeda === true;
  }

  private ladeTakedaStammdaten(): void {
    this.stammdatenLoading = true;

    forkJoin({
      orte: this.stammkundeService.getTakedaEinsatzorte(this.customerId)
        .pipe(catchError(() => of<TakedaEinsatzort[]>([]))),
      nummern: this.stammkundeService.getTakedaProjektnummern(this.customerId)
        .pipe(catchError(() => of<TakedaProjektnummer[]>([])))
    }).subscribe(result => {
      this.einsatzorte = result.orte ?? [];
      this.projektnummern = result.nummern ?? [];
      this.stammdatenLoading = false;
    });
  }

  private stammdatenFehler(err: any, standard: string): void {
    this.stammdatenSaving = false;
    this.snackBar.open(err?.error?.message || standard, 'OK', { duration: 6000 });
  }

  // Einsatzorte

  addEinsatzort(): void {
    const name = this.neuerEinsatzort.trim();
    if (!name || this.stammdatenSaving) { return; }

    this.stammdatenSaving = true;
    this.stammkundeService.createTakedaEinsatzort(this.customerId, {
      name,
      beschreibung: this.neuerEinsatzortBeschreibung.trim() || null,
      isActive: true,
      // Neue Orte hinten anstellen.
      sortOrder: this.einsatzorte.length
    }).subscribe({
      next: ort => {
        this.stammdatenSaving = false;
        this.einsatzorte = [...this.einsatzorte, ort];
        this.neuerEinsatzort = '';
        this.neuerEinsatzortBeschreibung = '';
        this.snackBar.open(`Einsatzort "${ort.name}" angelegt`, 'OK', { duration: 3000 });
      },
      error: err => this.stammdatenFehler(err, 'Der Einsatzort konnte nicht angelegt werden.')
    });
  }

  /** Aktiv/inaktiv umschalten – bestehende Aufträge bleiben unberührt. */
  toggleEinsatzortAktiv(ort: TakedaEinsatzort): void {
    if (this.stammdatenSaving) { return; }

    this.stammdatenSaving = true;
    this.stammkundeService.updateTakedaEinsatzort(this.customerId, ort.id, {
      name: ort.name,
      beschreibung: ort.beschreibung ?? null,
      isActive: !ort.isActive,
      sortOrder: ort.sortOrder
    }).subscribe({
      next: aktualisiert => {
        this.stammdatenSaving = false;
        this.einsatzorte = this.einsatzorte.map(o => o.id === aktualisiert.id ? aktualisiert : o);
      },
      error: err => this.stammdatenFehler(err, 'Der Einsatzort konnte nicht geändert werden.')
    });
  }

  deleteEinsatzort(ort: TakedaEinsatzort): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Einsatzort löschen',
        message: `"${ort.name}" wirklich löschen? Bestehende Aufträge behalten ihren `
          + 'Ortstext – nur die Auswahl verliert den Eintrag. Zum Ausblenden reicht '
          + 'auch "Deaktivieren".'
      }
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }

      this.stammkundeService.deleteTakedaEinsatzort(this.customerId, ort.id).subscribe({
        next: () => {
          this.einsatzorte = this.einsatzorte.filter(o => o.id !== ort.id);
          this.snackBar.open('Einsatzort gelöscht', 'OK', { duration: 3000 });
        },
        error: err => this.stammdatenFehler(err, 'Der Einsatzort konnte nicht gelöscht werden.')
      });
    });
  }

  // Übernahme aus "Anlagen & Verteiler"

  /**
   * Lädt die vorhandenen Equipment-Namen als Vorschlag. Nur lesend auf den
   * Wartungsbereich – nach der Übernahme sind beide Listen unabhängig.
   */
  oeffneUebernahme(): void {
    if (this.uebernahmeVorschlaege !== null) {
      this.uebernahmeVorschlaege = null;
      return;
    }

    this.uebernahmeLoading = true;
    this.uebernahmeAuswahl.clear();

    this.stammkundeService.getTakedaEinsatzortVorschlaege(this.customerId).subscribe({
      next: vorschlaege => {
        this.uebernahmeLoading = false;
        this.uebernahmeVorschlaege = vorschlaege ?? [];
        // Alles vorschlagen, was noch nicht angelegt ist.
        for (const v of this.uebernahmeVorschlaege) {
          if (!v.vorhandenAlsEinsatzort) {
            this.uebernahmeAuswahl.add(v.equipmentId);
          }
        }
      },
      error: err => {
        this.uebernahmeLoading = false;
        this.stammdatenFehler(err, 'Die Vorschläge konnten nicht geladen werden.');
      }
    });
  }

  istUebernahmeGewaehlt(equipmentId: number): boolean {
    return this.uebernahmeAuswahl.has(equipmentId);
  }

  toggleUebernahme(equipmentId: number): void {
    if (this.uebernahmeAuswahl.has(equipmentId)) {
      this.uebernahmeAuswahl.delete(equipmentId);
    } else {
      this.uebernahmeAuswahl.add(equipmentId);
    }
  }

  get uebernahmeAnzahl(): number {
    return this.uebernahmeAuswahl.size;
  }

  uebernehmeEinsatzorte(): void {
    const ids = [...this.uebernahmeAuswahl];
    if (ids.length === 0 || this.stammdatenSaving) { return; }

    this.stammdatenSaving = true;
    this.stammkundeService.uebernehmeTakedaEinsatzorte(this.customerId, ids).subscribe({
      next: neu => {
        this.stammdatenSaving = false;
        this.einsatzorte = [...this.einsatzorte, ...(neu ?? [])];
        this.uebernahmeVorschlaege = null;
        this.uebernahmeAuswahl.clear();
        const anzahl = neu?.length ?? 0;
        this.snackBar.open(
          anzahl === 0
            ? 'Alle gewählten Einträge waren schon vorhanden.'
            : `${anzahl} Einsatzort${anzahl === 1 ? '' : 'e'} übernommen`,
          'OK', { duration: 4000 });
      },
      error: err => this.stammdatenFehler(err, 'Die Übernahme ist fehlgeschlagen.')
    });
  }

  // Projektnummern

  addProjektnummer(): void {
    const nummer = this.neueProjektnummer.trim();
    if (!nummer || this.stammdatenSaving) { return; }

    this.stammdatenSaving = true;
    this.stammkundeService.createTakedaProjektnummer(this.customerId, {
      nummer,
      bezeichnung: this.neueProjektnummerBezeichnung.trim() || null,
      isActive: true
    }).subscribe({
      next: eintrag => {
        this.stammdatenSaving = false;
        this.projektnummern = [...this.projektnummern, eintrag];
        this.neueProjektnummer = '';
        this.neueProjektnummerBezeichnung = '';
        this.snackBar.open(`Projektnummer "${eintrag.nummer}" angelegt`, 'OK', { duration: 3000 });
      },
      error: err => this.stammdatenFehler(err, 'Die Projektnummer konnte nicht angelegt werden.')
    });
  }

  toggleProjektnummerAktiv(eintrag: TakedaProjektnummer): void {
    if (this.stammdatenSaving) { return; }

    this.stammdatenSaving = true;
    this.stammkundeService.updateTakedaProjektnummer(this.customerId, eintrag.id, {
      nummer: eintrag.nummer,
      bezeichnung: eintrag.bezeichnung ?? null,
      isActive: !eintrag.isActive
    }).subscribe({
      next: aktualisiert => {
        this.stammdatenSaving = false;
        this.projektnummern = this.projektnummern
          .map(p => p.id === aktualisiert.id ? aktualisiert : p);
      },
      error: err => this.stammdatenFehler(err, 'Die Projektnummer konnte nicht geändert werden.')
    });
  }

  deleteProjektnummer(eintrag: TakedaProjektnummer): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: {
        title: 'Projektnummer löschen',
        message: `"${eintrag.nummer}" wirklich löschen? Bereits zugeordnete Aufträge `
          + 'behalten ihre Nummer – nur die Auswahl verliert den Eintrag. Zum '
          + 'Ausblenden reicht auch "Deaktivieren".'
      }
    });

    ref.afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }

      this.stammkundeService.deleteTakedaProjektnummer(this.customerId, eintrag.id).subscribe({
        next: () => {
          this.projektnummern = this.projektnummern.filter(p => p.id !== eintrag.id);
          this.snackBar.open('Projektnummer gelöscht', 'OK', { duration: 3000 });
        },
        error: err => this.stammdatenFehler(err, 'Die Projektnummer konnte nicht gelöscht werden.')
      });
    });
  }

  openWartungDetail(): void {
    this.router.navigate(['/wartung/kunden', this.customerId]);
  }

  getLogoUrl(logoPath: string): string {
    return this.customerService.getLogoUrl(logoPath);
  }

  back(): void {
    this.router.navigate(['/stammkunden']);
  }
}
